/**
 * 型定義と純粋ヘルパーのみ。
 * すべてのデータは API (`./api.ts`) 経由で D1 から取得する。
 * (旧版のダミーデータは migrations/0003_seed_demo.sql に移行済み)
 */

export type GenreKey =
  | "romance"
  | "mystery"
  | "comedy"
  | "horror"
  | "fantasy"
  | "scifi"
  | "slice-of-life"
  | "other";

export type GenreTone =
  | "romance"
  | "mystery"
  | "comedy"
  | "horror"
  | "fantasy"
  | "scifi"
  | "slice"
  | "other";

export type AgeRating = "all" | "r15" | "r18";

export interface GenreInfo {
  ja: string;
  tone: GenreTone;
}

/** ジャンル定義(UI で参照する純粋なテーブル) */
export const GENRES: Record<GenreKey, GenreInfo> = {
  romance: { ja: "恋愛", tone: "romance" },
  mystery: { ja: "ミステリ", tone: "mystery" },
  comedy: { ja: "コメディ", tone: "comedy" },
  horror: { ja: "ホラー", tone: "horror" },
  fantasy: { ja: "ファンタジー", tone: "fantasy" },
  scifi: { ja: "SF", tone: "scifi" },
  "slice-of-life": { ja: "日常", tone: "slice" },
  other: { ja: "その他", tone: "other" },
};

export const GENRE_LIST: Array<{ key: "all" | GenreKey; ja: string }> = [
  { key: "all", ja: "すべて" },
  { key: "romance", ja: "恋愛" },
  { key: "mystery", ja: "ミステリ" },
  { key: "comedy", ja: "コメディ" },
  { key: "horror", ja: "ホラー" },
  { key: "fantasy", ja: "ファンタジー" },
  { key: "scifi", ja: "SF" },
  { key: "slice-of-life", ja: "日常" },
  { key: "other", ja: "その他" },
];

/** 英語表記(i18n 時にジャンルラベルを切り替える) */
export const GENRE_LABELS_EN: Record<GenreKey, string> = {
  romance: "Romance",
  mystery: "Mystery",
  comedy: "Comedy",
  horror: "Horror",
  fantasy: "Fantasy",
  scifi: "Sci-Fi",
  "slice-of-life": "Slice of life",
  other: "Other",
};

export interface Author {
  id: string;
  email?: string;
  displayName: string;
  handle: string;
  initials: string;
  bio: string;
  followers: number;
  workCount?: number;
  totalPlays: number;
  totalSales: number;
  joined: string;
  links: { twitter?: string; pixiv?: string; web?: string };
  createdAt?: number;
}

export interface WorkStats {
  views: number;
  plays: number;
  sales: number;
  favorites: number;
}

export interface Work {
  id: string;
  authorId: string;
  /** API レスポンスで JOIN されてくる作者情報(無い場合は authorId のみ) */
  authorName?: string;
  authorInitials?: string;
  title: string;
  shortDescription?: string;
  description?: string;
  genre: GenreKey;
  tags: string[];
  ageRating?: AgeRating;
  price?: number;
  estimatedPlayTime?: number;
  stats: WorkStats;
  rating?: number;
  reviews?: number;
  /** ISO 日付文字列 "2026-04-22"(API クライアントで ms から変換済み) */
  updatedAt: string;
  publishedAt?: string;
  pick?: boolean;
  isDraft?: boolean;
  isPrivate?: boolean;
  /** R2 上の相対パス。Portal は workImageUrl(work) で表示用 URL を解決 */
  thumbnail?: string;
  screenshots?: string[];
}

export interface Review {
  id: string;
  workId?: string;
  userId: string;
  userName: string;
  rating: 1 | 2 | 3 | 4 | 5;
  hasPurchased: boolean;
  createdAt: number;
  body: string;
}

export interface NotificationItem {
  id: string;
  type: "favorite" | "review" | "sale" | "system";
  text: string;
  at: string;
  workId?: string;
}

export interface MonthlySales {
  m: string;
  revenue: number;
}

export type LibraryStatus = "unplayed" | "in-progress" | "cleared";

export interface LibraryEntry {
  workId: string;
  purchasedAt: string;
  progress: number;
  status: LibraryStatus;
  isOwn?: boolean;
}

export interface RankEntry {
  workId: string;
  rank: number;
  prev: number | null;
}

export interface TagDef {
  name: string;
  count: number;
}

// =============================================================
// 純粋ヘルパー
// =============================================================

export function priceJP(p: number | undefined): string {
  if (!p || p === 0) return "無料";
  return "¥" + p.toLocaleString("ja-JP");
}

export function numJP(n: number): string {
  return n.toLocaleString("ja-JP");
}

export function genreLabel(g: GenreKey, lang: "ja" | "en"): string {
  if (lang === "en") return GENRE_LABELS_EN[g] ?? g;
  return GENRES[g]?.ja ?? g;
}
